import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useOwnProfile } from '_api/useProfile';
import { useAppNavigation } from '_navigator';
import { track } from '_utils/Amplitude';
import formatBookingTimeRange from '_utils/formatBookingTimeRange';
import { capitalizeFirstLetter } from '_utils/misc';
import { smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { UserListItem, PrimaryButton } from 'Components';
import { BookingSharingAvailability, BookingSharingRequest } from 'types/BookingSharing';
import getAvailabilityNonTakenSlots from './getAvailabilityNonTakenSlots';

const BookingSharingItem = ({
    actionable = true,
    hideContent = false,
    timeRange,
    end_at,
    start_at,
    creator_id,
    ...props
}: { actionable?: boolean | 'noButton'; hideContent?: boolean; timeRange?: string } & (
    | BookingSharingAvailability
    | BookingSharingRequest
)) => {
    const { theme } = useThemeContext();
    const { t } = useTranslation();
    const { navigate } = useAppNavigation();
    const ownId = useOwnProfile()?.data?.id;

    const assignedRequests = useMemo(() => ('assigned_requests' in props ? props.assigned_requests : []), [props]);

    const availableSlots = useMemo(() => {
        if ('assigned_requests' in props) {
            return getAvailabilityNonTakenSlots(
                { start_at, end_at, assigned_requests: props.assigned_requests },
                assignedRequests,
            );
        }
        return [];
    }, [props, assignedRequests, start_at, end_at]);

    const handlePress = () => {
        if (!actionable) {
            return;
        }
        if ('status' in props) {
            track('Pressed parking sharing request details');
            navigate('BookingSharingSelected', { type: 'request', requestId: props.id });
        } else {
            track('Pressed parking sharing availability details');
            navigate('BookingSharingSelected', { type: 'availability', availabilityId: props.id });
        }
    };

    const handleAccept = () => {
        if ('status' in props && props.status === 'requested') {
            track('Pressed accept parking sharing request');
            navigate('BookingSharingNew', {
                variant: 'availability',
                requestId: props.id,
            });
        }
    };

    const handleBook = () => {
        if ('assigned_requests' in props) {
            track('Pressed book parking sharing request');
            navigate('BookingSharingNew', {
                variant: 'request',
                availabilityId: props.id,
            });
        }
    };

    const timeString = useMemo(() => {
        if (timeRange) {
            return timeRange;
        }

        // If it's a request or availability with no assigned requests, show full time range
        if ('status' in props || props.assigned_requests.length === 0) {
            return capitalizeFirstLetter(
                formatBookingTimeRange(new Date(start_at), new Date(end_at), { t, includeOrdinal: true }),
            );
        }

        // For availability with assigned requests, calculate available time slots
        if ('assigned_requests' in props && props.assigned_requests.length > 0) {
            // If no available slots, show "taken"
            if (availableSlots.length === 0) {
                return t('bookingSharing:fully_booked');
            }

            // Format available slots
            const formattedSlots = availableSlots.map((slot) =>
                formatBookingTimeRange(slot.start, slot.end, { t, includeOrdinal: true }),
            );

            return capitalizeFirstLetter(formattedSlots.join(', '));
        }

        return '';
    }, [timeRange, props, start_at, end_at, t, availableSlots]);

    return (
        <View
            style={{
                flexDirection: 'row',
                alignItems: 'center',
            }}
        >
            <UserListItem
                id={creator_id}
                titleColor={theme.main}
                descColor={theme.main}
                desc={hideContent ? undefined : timeString}
                containerStyle={{ paddingBottom: 0, flex: 1 }}
                actionButton={
                    creator_id !== ownId &&
                    actionable === true &&
                    ('status' in props || availableSlots.length !== 0) ? (
                        <View style={{ justifyContent: 'center' }}>
                            {'status' in props ? (
                                props.status === 'requested' ? (
                                    <PrimaryButton
                                        style={{ paddingTop: smallestMargin, paddingBottom: smallestMargin }}
                                        text={t('bookingSharing:accept')}
                                        onPress={handleAccept}
                                    />
                                ) : null
                            ) : availableSlots.length !== 0 ? (
                                <PrimaryButton
                                    text={t('bookingSharing:book')}
                                    style={{ paddingTop: smallestMargin, paddingBottom: smallestMargin }}
                                    onPress={handleBook}
                                />
                            ) : null}
                        </View>
                    ) : undefined
                }
                onPress={handlePress}
                noBottomBorder
            />
        </View>
    );
};

export default BookingSharingItem;
